import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Checkbox, Loader } from 'ncoded-component-library';
import RichTextEditorField from 'components/RichTextEditorField';
import validations from 'validations';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import { ColdLeadAggregationResult } from 'router/subrouters/DataRoom/models/Dashboard';
import { getColdLeadUserInfo } from '../../utils';
import {
  createRecordFromKeys,
  getArrayLength,
  getCheckedValuesInObject,
  getIsArrayEmpty,
  getUserFullNameOrEmail,
  isSingleElementArray,
} from 'utils';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';

import './BroadcastMessageForm.styles.scss';

const MAX_NUM_OF_CHAR = 100000;

type FormValues = {
  message: string;
  dataRoomMemberIds: Record<string, boolean>;
  sendInvitationEmail: boolean;
};

type BroadcastMessageFormProps = {
  className?: string;
  isBroadcast: boolean;
  dataRoomMembers: Partial<ColdLeadAggregationResult>[];
  onClose: () => void;
};

const BroadcastMessageForm: React.FC<BroadcastMessageFormProps> = (props) => {
  const { className, dataRoomMembers, isBroadcast, onClose } = props;

  const classes = classNames('broadcast-message-form', className);

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const dataRoomMemberIds = useMemo(
    () => dataRoomMembers.map(({ dataRoomMemberId }) => dataRoomMemberId),
    [dataRoomMembers],
  );

  const fullMembersLength = getArrayLength(dataRoomMemberIds);
  const isSingleElement = isSingleElementArray(dataRoomMemberIds);

  const initialValues = useMemo(
    () => ({
      dataRoomMemberIds: createRecordFromKeys(dataRoomMemberIds, true),
    }),
    [dataRoomMemberIds],
  );

  const options = useMemo(
    () =>
      dataRoomMembers.map((lead) => ({
        value: lead.dataRoomMemberId,
        label: getColdLeadUserInfo(lead),
      })),
    [dataRoomMembers],
  );

  const onSubmit = useCallback(
    async ({ message, dataRoomMemberIds, sendInvitationEmail }: FormValues) => {
      const selectedDataRoomMemberIds =
        getCheckedValuesInObject(dataRoomMemberIds);

      try {
        await dataRoomApi.room.coldLeadsBroadcast(dataRoomId, {
          message,
          sendInvitationEmail,
          dataRoomMemberIds: selectedDataRoomMemberIds,
        });

        const singleRecipient = isSingleElementArray(selectedDataRoomMemberIds)
          ? dataRoomMembers.find(
              ({ dataRoomMemberId }) =>
                dataRoomMemberId === selectedDataRoomMemberIds[0],
            )
          : undefined;

        popSuccess(
          singleRecipient
            ? t('messageDeliveredToLead', {
                lead: getUserFullNameOrEmail(singleRecipient),
              })
            : t('broadcastMessageToAllLeadsSuccessfully'),
        );
        onClose();
      } catch (error) {
        popServerError(error);
      }
    },
    [dataRoomId, dataRoomMembers, onClose, popServerError, popSuccess, t],
  );

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ form, submitting, invalid, values, handleSubmit }) => {
        const selectedDataRoomMemberIds = getCheckedValuesInObject(
          values.dataRoomMemberIds,
        );

        const count = getArrayLength(selectedDataRoomMemberIds);
        const isAllSelected = count === fullMembersLength;

        return (
          <form onSubmit={handleSubmit} className={classes}>
            {submitting && <Loader />}

            {isBroadcast && (
              <>
                <div
                  className={classNames(
                    'broadcast-message-form__checkbox',
                    'white-panel',
                  )}
                >
                  <p>{`${t('recipients')} (${count}/${fullMembersLength})`}</p>
                  <Checkbox
                    className="select-all"
                    checked={isAllSelected}
                    label={t('selectAll')}
                    onChange={() =>
                      form.batch(() =>
                        form.change(
                          'dataRoomMemberIds',
                          createRecordFromKeys(
                            dataRoomMemberIds,
                            !isAllSelected,
                          ),
                        ),
                      )
                    }
                  />

                  <CheckboxFieldGroup
                    name="dataRoomMemberIds"
                    options={options}
                    className="broadcast-message-form__leads"
                  />
                </div>
              </>
            )}

            <RichTextEditorField
              name="message"
              required
              label={t('message')}
              showMaxLength={false}
              maxLength={MAX_NUM_OF_CHAR}
              validate={validations.richEditorRequired()}
            />

            <Field
              name="sendInvitationEmail"
              type="checkbox"
              component={CheckboxField}
              label={t(
                isSingleElement
                  ? 'broadcastThisMessageViaEmail'
                  : 'broadcastThisMessageViaEmailAllSelectedLeads',
              )}
            />

            <div className="actions">
              <Button variant="link" onClick={onClose}>
                {t('cancel')}
              </Button>

              <Button
                type="submit"
                disabled={
                  submitting ||
                  invalid ||
                  getIsArrayEmpty(selectedDataRoomMemberIds)
                }
              >
                {t('send')}
              </Button>
            </div>
          </form>
        );
      }}
    />
  );
};

export default BroadcastMessageForm;
